import posthog from 'posthog-js';
import { COOKIE_CONSENT_EVENT } from '../utils/cookieConsent';
import * as Sentry from '@sentry/react';
import { isPrerendering } from '../utils/prerendering';
import { DASHBOARD_ROUTES } from '../constants/dashboardRoutes';

/** Collapse `/` → `/home` → `/home?v=a` (and similar SPA redirects) into one page_visit. */
export const PAGE_VIEW_DEBOUNCE_MS = 350;

/** Effect-driven events that re-fire on remount / unstable hook deps. */
export const EFFECT_EVENT_DEDUPE_MS = 1500;

const EFFECT_DRIVEN_EVENT_TYPES = new Set([
  'onboarding_completed',
  'user_logged_in',
]);

const REDIRECT_ONLY_PAGES = ['/', '/landing'];

/**
 * Analytics Service for PrecisionTrader
 * Handles page views and user interactions
 * Integrates with PostHog for product analytics
 *
 * PostHog pageviews and interactions are always captured (even when user rejects optional cookies)
 * to ensure correct product analytics. Session recording and marketing tools respect consent.
 */

// Check if running locally - used to disable PostHog in development
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
// Allow opt-in PostHog tracking on localhost for testing
const enablePosthogLocal = process.env.REACT_APP_ENABLE_POSTHOG_LOCAL === 'true';
// Disable PostHog during pre-rendering to avoid network errors
const isPosthogDisabled = isPrerendering() || (isLocalHost && !enablePosthogLocal);

// Normalize landing version to simple number string
const normalizeLandingVersion = (v) => {
  if (!v) return null;
  const s = String(v).trim().toLowerCase();
  if (s === 'v1' || s === '1') return '1';
  if (s === 'v2' || s === '2') return '2';
  if (s === 'v3' || s === '3') return '3';
  return null;
};

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    this.userId = null;
    this.userToken = null;
    this.startTime = Date.now();
    this.lastTrackedPage = null; // Track last page
    this.lastTrackedTime = 0; // Track last tracking time
    this.lastSentPageKey = null; // Dedupe: last pageKey we sent page_visit for
    this.lastSentPageVisitTime = 0; // Dedupe: time of last page_visit send (avoids Strict Mode / redirect double-fires)
    this.lastSentPosthogPageKey = null; // Dedupe: last pageKey we sent $pageview for
    this.lastSentPosthogPageViewTime = 0; // Dedupe: time of last $pageview send
    this.lastIdentifiedUserId = null; // Dedupe: skip PostHog identify if same user already identified this session
    this.isLocalHost = isPosthogDisabled; // Track if PostHog should be disabled
    this.pageViewDebounceMs = PAGE_VIEW_DEBOUNCE_MS;
    this._pageViewTimer = null;
    this._pendingPageView = null;
    this._finalVisitFlushSent = false;
    this._lastTradeEventKey = null;
    this._lastTradeEventTime = 0;
    
    // Page time tracking (initialize before getOrCreateSessionId)
    this.pageStartTimes = new Map(); // Track when user entered each page (pageKey -> timestamp)
    this.pageStates = new Map(); // Track accumulated state per page (pagePath -> {accumulatedTime, maxScroll, lastVisitStart})
    this.lastActiveTime = Date.now(); // Last time user was active
    this.isPageVisible = true; // Track if page is visible
    
    // Scroll tracking (initialize before getOrCreateSessionId)
    this.scrollTracking = new Map(); // Track scroll depth milestones per page
    
    // Get or create session ID (after Maps are initialized)
    this.sessionId = this.getOrCreateSessionId();
    
    // Pages that can be tracked without authentication
    this.publicPages = ['/trade', '/', '/home', '/pricing', '/landing', '/register', '/login', '/features', '/flash-sale', '/brokers'];
    
    // Initialize visibility and unload handlers
    this.initVisibilityHandlers();

    // Ensure PostHog always captures pageviews and interactions (even when user rejects optional cookies)
    this.initPostHogConsentSync();
    
    // Check for midnight rotation
    this.checkMidnightRotation();
  }

  /**
   * Ensure PostHog captures pageviews and interactions regardless of cookie consent.
   * When user rejects optional cookies, we still need product analytics. We explicitly opt in
   * so PostHog continues to receive $pageview and interaction events.
   */
  initPostHogConsentSync() {
    const ensurePostHogCapturing = () => {
      if (typeof posthog === 'undefined' || this.isLocalHost) return;
      try {
        if (posthog.has_opted_out_capturing && posthog.has_opted_out_capturing()) {
          posthog.opt_in_capturing();
        }
      } catch (e) {
        // PostHog may not be initialized yet
      }
    };

    // Ensure capturing on init (e.g. user previously rejected, page reloaded)
    ensurePostHogCapturing();

    // When cookie consent changes (accept/reject), ensure we stay opted in
    window.addEventListener(COOKIE_CONSENT_EVENT, ensurePostHogCapturing);
  }

  /**
   * Best-effort home page version for attribution (1/2).
   * Priority:
   * - Query string (?v=1 / ?v=2)
   * - sessionStorage home_page_version (set by HomePageRouter)
   * - sessionStorage landing_page_version (backward compatibility)
   * - ENV override (REACT_APP_DEFAULT_HOME_VERSION)
   */
  getLandingPageVersion(pagePath, searchString) {
    const search = searchString !== null && searchString !== undefined ? searchString : window.location.search;
    try {
      const params = new URLSearchParams(search || '');
      const qp = params.get('v');
      const normalized = normalizeLandingVersion(qp);
      if (normalized) return normalized;
    } catch (e) {
      // ignore URLSearchParams issues
    }

    try {
      // Try home_page_version first
      const stored = sessionStorage.getItem('home_page_version');
      const normalized = normalizeLandingVersion(stored);
      if (normalized) return normalized;
      
      // Fallback to landing_page_version for backward compatibility
      const legacyStored = sessionStorage.getItem('landing_page_version');
      const legacyNormalized = normalizeLandingVersion(legacyStored);
      if (legacyNormalized) return legacyNormalized;
    } catch (e) {
      // sessionStorage not available
    }

    const envVersion = normalizeLandingVersion(process.env.REACT_APP_DEFAULT_HOME_VERSION);
    return envVersion || null;
  }
  
  /**
   * Initialize Page Visibility API and unload handlers for session duration tracking
   */
  initVisibilityHandlers() {
    // Track visibility changes - detect when tab becomes hidden (user leaving site)
    document.addEventListener('visibilitychange', () => {
      const wasVisible = this.isPageVisible;
      this.isPageVisible = !document.hidden;
      
      if (!document.hidden && wasVisible === false) {
        // Tab became visible again after being hidden
        // Check if we need a new session (tab was closed/reopened or hidden too long)
        const timeHidden = Date.now() - this.lastActiveTime;
        const thirtyMinutes = 30 * 60 * 1000; // 30 minutes
        
        // Check if sessionStorage still has our session (if not, tab was closed)
        try {
          const storedSession = sessionStorage.getItem('analytics_session_id');
          if (!storedSession) {
            // SessionStorage was cleared (tab closed), start new session
            this.rotateSession('tab_closed');
          } else if (timeHidden > thirtyMinutes) {
            // Tab was hidden for more than 30 minutes, start new session
            this.rotateSession('tab_hidden_too_long');
          } else {
            // Same session, just update active time
            this.lastActiveTime = Date.now();
          }
        } catch (e) {
          // sessionStorage not available, continue with current session
          this.lastActiveTime = Date.now();
        }
      } else if (document.hidden) {
        // Tab is now hidden - mark last active time
        this.lastActiveTime = Date.now();
        // Save current session state before tab might close
        this.saveSessionId();
      }
    });
    
    // Track before page unload to update current page visit with final data
    // Use beforeunload as primary handler (desktop browsers)
    window.addEventListener('beforeunload', () => {
      this.updateCurrentPageVisit();
      // Save session state before unload
      this.saveSessionId();
    });
    
    // pagehide is a fallback for mobile browsers
    window.addEventListener('pagehide', () => {
      this.updateCurrentPageVisit();
      this.saveSessionId();
    });
    
    // Detect when user navigates away from the site (e.g., to another domain)
    // This is detected by checking if we're still on the same origin
    window.addEventListener('blur', () => {
      this.lastActiveTime = Date.now();
    });
  }
  
  
  /**
   * Update current page visit before page closes
   * Sends final time_spent and scroll_percent for the current page
   */
  updateCurrentPageVisit() {
    // beforeunload + pagehide both fire on many browsers; send the final visit once.
    if (this._finalVisitFlushSent) return;
    if (!this.lastTrackedPage || !this.pageStartTimes.has(this.lastTrackedPage)) {
      return;
    }
    
    const pageStart = this.pageStartTimes.get(this.lastTrackedPage);
    const pagePath = this.lastTrackedPage.split('?')[0];
    const timeOnThisVisit = Math.floor((Date.now() - pageStart) / 1000);
    const maxScroll = this.getMaxScrollDepth(pagePath);
    
    // Get or create page state
    const currentState = this.pageStates.get(pagePath) || {
      accumulatedTime: 0,
      maxScroll: 0,
      lastVisitStart: pageStart
    };
    
    // Accumulate time for this visit
    const newAccumulatedTime = currentState.accumulatedTime + timeOnThisVisit;
    const newMaxScroll = Math.max(currentState.maxScroll, maxScroll);
    
    if (timeOnThisVisit > 0) {
      this._finalVisitFlushSent = true;
      // Update the current page_visit record with accumulated data
      this.sendToBackend('page_visit', {
        page_path: pagePath,
        page_name: this.getPageName(pagePath),
        time_spent: newAccumulatedTime, // Send accumulated time
        scroll_percent: newMaxScroll, // Send max scroll
        session_id: this.sessionId,
        user_id: this.userId,
        timestamp: new Date().toISOString()
      }, null, true); // Use beacon for reliable delivery on page unload
    }
  }

  /**
   * Initialize analytics service
   */
  init(userId = null, userToken = null) {
    this.userId = userId;
    this.userToken = userToken;
    this.isInitialized = true;
    
    // Identify user in PostHog when user ID is available
    if (userId) {
      this.identifyUser(userId);
    }
    
    // Note: Page view tracking is handled by useAnalytics hook on route changes
    // No need to track here to avoid duplicate events
  }

  /**
   * Identify user in PostHog for user-level analytics
   * Call this when user logs in or when user data becomes available
   * @param {string} userId - The user's unique identifier
   * @param {object} traits - Optional user properties/traits
   */
  identifyUser(userId, traits = {}) {
    if (!userId) return;

    // Dedupe: only identify once per userId per session (Strict Mode + Auth + useAnalytics can call multiple times on load)
    if (this.lastIdentifiedUserId === userId) return;
    this.lastIdentifiedUserId = userId;

    // Skip PostHog when disabled (default on localhost)
    if (this.isLocalHost) {
      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog: Skipped identify (localhost)', { userId });
      }
      return;
    }
    
    try {
      // Get user data from localStorage for additional traits
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      
      const userTraits = {
        ...traits,
        subscription_status: userData.subscriptionStatus || 'unknown',
        has_tradestation: userData.hasTradeStationCredentials || false,
        early_access: userData.early_access || false,
        beta_user: userData.beta_user || false,
      };
      
      // Identify user in PostHog
      posthog.identify(userId, userTraits);
    } catch (error) {
      console.error('PostHog identify error:', error);
    }
  }

  /**
   * Reset session tracking - clears session data and generates new session ID
   * Called on logout to prevent source tracking from being attributed to wrong user
   */
  resetSession() {
    // Clear sessionStorage tracking data
    try {
      sessionStorage.removeItem('registration_source');
      sessionStorage.removeItem('utm_params');
    } catch (e) {
      // sessionStorage not available (e.g., in private browsing)
    }
    
    // Reset PostHog to clear user identity (skip in local development)
    if (!this.isLocalHost) {
      try {
        posthog.reset();
        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog: User reset (logout)');
        }
      } catch (error) {
        console.error('PostHog reset error:', error);
      }
    }
    
    // Rotate session (generates new ID and saves it)
    this.rotateSession('logout');
    this.lastTrackedPage = null;
    this.lastTrackedTime = 0;
    this.lastIdentifiedUserId = null;
    
    // Clear user-specific data but keep service initialized
    this.userId = null;
    this.userToken = null;
  }

  /**
   * Get or create session ID with rotation logic
   * Checks for midnight rotation and tab close detection
   */
  getOrCreateSessionId() {
    try {
      const stored = sessionStorage.getItem('analytics_session_id');
      const storedDate = sessionStorage.getItem('analytics_session_date');
      
      if (stored && storedDate) {
        // Check if we've crossed midnight
        const storedDateObj = new Date(storedDate);
        const now = new Date();
        
        // Check if it's a different day
        if (storedDateObj.toDateString() !== now.toDateString()) {
          // Midnight has passed, rotate session
          return this.rotateSession('midnight');
        }
        
        // Session exists and is still valid
        return stored;
      }
    } catch (e) {
      // sessionStorage not available (e.g., in private browsing)
    }
    
    // No stored session or error - create new one (tab was closed or first visit)
    return this.rotateSession('new');
  }
  
  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
  
  /**
   * Rotate session ID (create new session)
   * @param {string} reason - Reason for rotation (for logging/debugging)
   */
  rotateSession(reason = 'unknown') {
    // Clear old tracking data
    this.pageStartTimes.clear();
    this.pageStates.clear();
    this.scrollTracking.clear();
    this.lastTrackedPage = null;
    this.lastTrackedTime = 0;
    this.lastSentPageKey = null;
    this.lastSentPageVisitTime = 0;
    this.lastSentPosthogPageKey = null;
    this.lastSentPosthogPageViewTime = 0;
    this.lastIdentifiedUserId = null;
    this._finalVisitFlushSent = false;
    this._lastTradeEventKey = null;
    this._lastTradeEventTime = 0;
    this._pendingPageView = null;
    if (this._pageViewTimer) {
      clearTimeout(this._pageViewTimer);
      this._pageViewTimer = null;
    }

    // Generate new session ID
    const newSessionId = this.generateSessionId();
    this.sessionId = newSessionId;
    
    // Save to sessionStorage
    this.saveSessionId();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Analytics session rotated:', reason, newSessionId);
    }
    
    return newSessionId;
  }
  
  /**
   * Save session ID to sessionStorage
   */
  saveSessionId() {
    try {
      sessionStorage.setItem('analytics_session_id', this.sessionId);
      sessionStorage.setItem('analytics_session_date', new Date().toISOString());
      sessionStorage.setItem('analytics_last_active', Date.now().toString());
    } catch (e) {
      // sessionStorage not available
    }
  }
  
  /**
   * Check if we've crossed midnight and rotate session if needed
   */
  checkMidnightRotation() {
    try {
      const storedDate = sessionStorage.getItem('analytics_session_date');
      if (storedDate) {
        const storedDateObj = new Date(storedDate);
        const now = new Date();
        
        // Check if it's a different day
        if (storedDateObj.toDateString() !== now.toDateString()) {
          // Midnight has passed, rotate session
          this.rotateSession('midnight');
        }
      }
    } catch (e) {
      // sessionStorage not available
    }
    
    // Schedule next midnight check (check every hour)
    setTimeout(() => {
      this.checkMidnightRotation();
    }, 60 * 60 * 1000); // 1 hour
  }

  /**
   * Check if a page is public (can be tracked without auth)
   */
  isPublicPage(pagePath) {
    return this.publicPages.some(p => pagePath === p || pagePath.startsWith(p + '/') || pagePath.startsWith(p + '?'));
  }

  isRedirectOnlyPage(pagePath) {
    const pathOnly = (pagePath || '').split('?')[0];
    return REDIRECT_ONLY_PAGES.includes(pathOnly);
  }

  /**
   * Extract UTM parameters from URL
   * Returns an object with utm_source, utm_medium, utm_campaign, utm_term, utm_content
   * Only extracts from current URL, not from sessionStorage (to avoid duplicate tracking)
   * @param {string} searchString - Optional search string (e.g., from React Router location.search)
   *                                If not provided, uses window.location.search
   * @param {boolean} storeInSession - If true, stores UTM params in sessionStorage for future reference
   */
  extractUTMParameters(searchString = null, storeInSession = true) {
    // Use provided search string (from React Router) or fall back to window.location.search
    const search = searchString !== null ? searchString : window.location.search;
    const params = new URLSearchParams(search);
    const utmParams = {};
    
    // Standard UTM parameters
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    utmKeys.forEach(key => {
      const value = params.get(key);
      if (value) {
        utmParams[key] = value;
      }
    });
    
    // If UTM params exist in URL, optionally store them in sessionStorage for future reference
    // (but don't use them for tracking subsequent page views)
    if (Object.keys(utmParams).length > 0 && storeInSession) {
      try {
        sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
      } catch (e) {
        // sessionStorage not available (e.g., in private browsing)
      }
      return utmParams;
    }
    
    // Return UTM params if found in URL, otherwise return null
    // Don't check sessionStorage - only track UTM params when they're actually in the URL
    return Object.keys(utmParams).length > 0 ? utmParams : null;
  }

  /**
   * Extract source tracking parameters from URL
   * Returns an object with source tracking data including UTM params and custom source param
   * This is used for registration/login source tracking
   * @param {string} searchString - Optional search string (e.g., from React Router location.search)
   *                                If not provided, uses window.location.search
   *                                Can be full query string like "?source=reddit" or just "source=reddit"
   * @param {boolean} storeInSession - If true, stores source params in sessionStorage for future reference
   */
  extractSourceParameters(searchString = null, storeInSession = true) {
    // Use provided search string (from React Router) or fall back to window.location.search
    let search = searchString !== null ? searchString : window.location.search;
    
    // Ensure search string starts with ? if it has content (URLSearchParams expects this format)
    if (search && !search.startsWith('?') && search.includes('=')) {
      search = '?' + search;
    }
    
    const params = new URLSearchParams(search);
    const sourceParams = {};
    
    // Standard UTM parameters
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    utmKeys.forEach(key => {
      const value = params.get(key);
      if (value) {
        sourceParams[key] = value;
      }
    });
    
    // Custom source parameter (e.g., ?source=reddit)
    const customSource = params.get('source');
    if (customSource) {
      sourceParams.source = customSource;
    }
    
    // If source params exist in URL, optionally store them in sessionStorage for future reference
    if (Object.keys(sourceParams).length > 0) {
      if (storeInSession) {
        try {
          sessionStorage.setItem('registration_source', JSON.stringify(sourceParams));
        } catch (e) {
          // sessionStorage not available (e.g., in private browsing)
        }
      }
      return sourceParams;
    }
    
    // No params in URL - check sessionStorage as fallback (always check, regardless of storeInSession)
    // This ensures we can retrieve stored params even when called with storeInSession=false
    try {
      const stored = sessionStorage.getItem('registration_source');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Return stored params if they exist
        if (parsed && Object.keys(parsed).length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      // sessionStorage not available or invalid JSON
    }
    
    return null;
  }

  /**
   * Track page views - Consolidated approach
   * Creates a single page_visit event that gets updated with time_spent and scroll_percent
   * Public pages (trade, landing, pricing) are tracked without requiring auth
   * @param {string} pagePath - The page path (e.g., '/trade', '/')
   * @param {string} pageTitle - Optional page title
   * @param {string} searchString - Optional search string from React Router (e.g., '?utm_source=reddit')
   */
  trackPageView(pagePath, pageTitle = null, searchString = null) {
    if (!this.isInitialized) return;
    if (this.isRedirectOnlyPage(pagePath)) return;

    // For non-public pages, require user authentication
    if (!this.isPublicPage(pagePath) && !this.userId) return;

    this._pendingPageView = { pagePath, pageTitle, searchString };
    if (this.pageViewDebounceMs <= 0) {
      this.flushPageView(pagePath, pageTitle, searchString);
      return;
    }
    if (this._pageViewTimer) {
      clearTimeout(this._pageViewTimer);
    }
    this._pageViewTimer = setTimeout(() => {
      const pending = this._pendingPageView;
      this._pendingPageView = null;
      this._pageViewTimer = null;
      if (pending) {
        this.flushPageView(pending.pagePath, pending.pageTitle, pending.searchString);
      }
    }, this.pageViewDebounceMs);
  }

  /**
   * Emit a page_visit after debounce. Consecutive calls for the same pathname
   * (query-only changes, remounts) are not sent again.
   */
  flushPageView(pagePath, pageTitle = null, searchString = null) {
    if (!this.isInitialized) return;
    if (this.isRedirectOnlyPage(pagePath)) return;
    if (!this.isPublicPage(pagePath) && !this.userId) return;

    const currentSearch = searchString !== null ? searchString : window.location.search;
    const pageKey = `${pagePath}${currentSearch}`;
    const now = Date.now();
    const pagePathKey = pagePath.split('?')[0]; // Remove query params for page path
    const lastPath = this.lastTrackedPage ? this.lastTrackedPage.split('?')[0] : null;

    // Same pathname as the visit already in progress (e.g. `/home` → `/home?v=a`)
    if (lastPath === pagePathKey) {
      this.lastTrackedPage = pageKey;
      return;
    }

    this._finalVisitFlushSent = false;
    
    // Update previous page visit with final time_spent and scroll_percent before switching
    if (this.lastTrackedPage && this.pageStartTimes.has(this.lastTrackedPage)) {
      const previousPageStart = this.pageStartTimes.get(this.lastTrackedPage);
      const previousPagePath = this.lastTrackedPage.split('?')[0];
      const timeOnThisVisit = Math.floor((now - previousPageStart) / 1000); // seconds for this visit
      
      // Get max scroll depth for previous page
      const maxScroll = this.getMaxScrollDepth(previousPagePath);
      
      // Get or create page state for previous page
      const previousState = this.pageStates.get(previousPagePath) || {
        accumulatedTime: 0,
        maxScroll: 0,
        lastVisitStart: previousPageStart
      };
      
      // Accumulate time for this visit
      const newAccumulatedTime = previousState.accumulatedTime + timeOnThisVisit;
      const newMaxScroll = Math.max(previousState.maxScroll, maxScroll);
      
      // Update page state
      this.pageStates.set(previousPagePath, {
        accumulatedTime: newAccumulatedTime,
        maxScroll: newMaxScroll,
        lastVisitStart: previousPageStart
      });
      
      // Update the previous page_visit record with accumulated data
      if (timeOnThisVisit > 0) {
        this.sendToBackend('page_visit', {
          page_path: previousPagePath,
          page_name: this.getPageName(previousPagePath),
          time_spent: newAccumulatedTime, // Send accumulated time
          scroll_percent: newMaxScroll, // Send max scroll across all visits
          session_id: this.sessionId,
          user_id: this.userId,
          timestamp: new Date().toISOString()
        });
      }
      
      // Remove previous page from active tracking (but keep state)
      this.pageStartTimes.delete(this.lastTrackedPage);
      // Don't delete scrollTracking - we keep it for max scroll calculation
    }
    
    // Check if we've visited this page before in this session
    const existingState = this.pageStates.get(pagePathKey);
    const isReturnVisit = !!existingState;
    
    // Track start time for this page visit
    this.pageStartTimes.set(pageKey, now);
    this.lastActiveTime = now;
    
    // Reset scroll tracking milestones for this visit (but keep max scroll in state)
    this.resetScrollTracking();

    // Ensure session_id is always available
    const sessionId = this.sessionId || this.generateSessionId();
    if (!this.sessionId) {
      this.sessionId = sessionId;
    }

    // Extract UTM parameters from URL (use provided search string or window.location.search)
    const utmParams = this.extractUTMParameters(currentSearch, true);
    
    // Also extract and store source parameters for registration tracking
    const sourceParams = this.extractSourceParameters(currentSearch, true);

    // Get page name for display (extract from path)
    const pageName = this.getPageName(pagePath);
    
    // Initialize or update page state
    if (!isReturnVisit) {
      // First visit to this page - initialize state
      this.pageStates.set(pagePathKey, {
        accumulatedTime: 0,
        maxScroll: 0,
        lastVisitStart: now
      });
    } else {
      // Return visit - update lastVisitStart but keep accumulated time
      this.pageStates.set(pagePathKey, {
        ...existingState,
        lastVisitStart: now
      });
    }
    
    // Create/update page_visit event with current accumulated state
    // Backend uses UPSERT based on session_id + page_path, so this won't create duplicates
    const currentState = this.pageStates.get(pagePathKey);
    const pageData = {
      page_path: pagePathKey, // Use path without query params for consistency
      page_name: pageName,
      page_title: pageTitle || document.title,
      page_location: window.location.href,
      session_id: sessionId,
      user_id: this.userId,
      timestamp: new Date().toISOString(),
      time_spent: currentState.accumulatedTime, // Current accumulated time (0 for new visit, existing for return)
      scroll_percent: currentState.maxScroll, // Max scroll across all visits
      // Only include UTM params if they're actually in the current URL
      ...(utmParams && { utm_params: utmParams }),
      // Include source parameters (from URL or sessionStorage) for tracking
      ...(sourceParams && { registration_source: sourceParams, source: sourceParams })
    };

    // PostHog pageview (manual, so we can attach landing variant)
    // For /home, wait until landing version is known to avoid an initial "unknown" pageview.
    if (!this.isLocalHost) {
      try {
        const landingPageVersion = this.getLandingPageVersion(pagePathKey, currentSearch);
        const isHome = pagePathKey === '/home' || pagePathKey === '/';
        const shouldCapture = !isHome || !!landingPageVersion;

        // Dedupe: skip sending same pageview within 300ms
        const samePosthogPageRecently =
          this.lastSentPosthogPageKey === pageKey && (now - this.lastSentPosthogPageViewTime) < 300;

        if (shouldCapture && !samePosthogPageRecently) {
          if (landingPageVersion) {
            // Ensure future events automatically include the assigned variant
            posthog.register({ landing_page_version: landingPageVersion });
          }

          // Helpful for filtering local test data in PostHog
          if (isLocalHost) {
            posthog.register({ app_env: 'local' });
          }

          posthog.capture('$pageview', {
            page_path: pagePathKey,
            page_name: pageName,
            page_title: pageData.page_title,
            page_location: pageData.page_location,
            user_type: this.getUserType(),
            landing_page_version: landingPageVersion || undefined,
            app_env: isLocalHost ? 'local' : undefined,
            ...(utmParams && { utm_params: utmParams }),
            ...(sourceParams && { registration_source: sourceParams, source: sourceParams }),
          });

          this.lastSentPosthogPageKey = pageKey;
          this.lastSentPosthogPageViewTime = now;
        }
      } catch (error) {
        console.error('PostHog pageview capture error:', error);
      }
    }

    // Google Analytics
    if (typeof gtag !== 'undefined') {
      // eslint-disable-next-line no-undef
      gtag('config', this.gaId, {
        page_path: pagePath,
        page_title: pageData.page_title,
        custom_map: {
          'custom_parameter_1': this.userId ? 'authenticated' : 'anonymous',
          'custom_parameter_2': this.getUserType()
        }
      });
    }

    // Dedupe: skip sending same page_visit within 300ms (Strict Mode double-invoke + redirect can fire 4x on load)
    const samePageRecently = this.lastSentPageKey === pageKey && (now - this.lastSentPageVisitTime) < 300;
    if (!samePageRecently) {
      this.sendToBackend('page_visit', pageData);
      this.lastSentPageKey = pageKey;
      this.lastSentPageVisitTime = now;
    }

    // Update tracking state (always, so previous-page logic works on real navigations)
    this.lastTrackedPage = pageKey;
    this.lastTrackedTime = now;
  }
  
  /**
   * Get maximum scroll depth reached for a page
   */
  getMaxScrollDepth(pagePath) {
    let maxScroll = 0;
    for (const [key, value] of this.scrollTracking.entries()) {
      if (key.startsWith(pagePath)) {
        const scrollPercent = parseInt(key.split('_').pop()) || 0;
        if (scrollPercent > maxScroll) {
          maxScroll = scrollPercent;
        }
      }
    }
    return maxScroll;
  }

  /**
   * Track trading-specific events
   * Sends each event with its own distinct event_type to the backend
   * 
   * Supported event types:
   * - buy_button_pressed / sell_button_pressed
   * - close_position / cancel_orders
   * - journal_entry_created
   * - max_loss_setting_enabled
   * - indicator_added
   * - watchlist_created / watchlist_ticker_added
   * - bracket_type_changed
   * - alert_created
   * - account_switched
   * - settings_updated
   * 
   * Events that automatically include session source:
   * - early_access_button_clicked
   * - user_registered
   */
  trackTradingEvent(eventType, tradeData = {}) {
    if (!this.isInitialized) {
      // Try to initialize if not already done
      this.init(this.userId, this.userToken);
    }
    
    // Validate eventType is provided
    if (!eventType || typeof eventType !== 'string' || eventType.trim() === '') {
      console.error('Invalid eventType provided to trackTradingEvent:', eventType);
      return;
    }

    // Effect-driven events re-fire when App remounts or hook deps churn.
    if (EFFECT_DRIVEN_EVENT_TYPES.has(eventType)) {
      let payloadKey = '';
      try {
        payloadKey = JSON.stringify(tradeData || {});
      } catch (e) {
        payloadKey = String(tradeData);
      }
      const dedupeKey = `${eventType}|${payloadKey}`;
      const now = Date.now();
      if (
        this._lastTradeEventKey === dedupeKey &&
        (now - this._lastTradeEventTime) < EFFECT_EVENT_DEDUPE_MS
      ) {
        return;
      }
      this._lastTradeEventKey = dedupeKey;
      this._lastTradeEventTime = now;
    }

    // Ensure session_id is always available
    const sessionId = this.sessionId || this.generateSessionId();
    if (!this.sessionId) {
      this.sessionId = sessionId;
    }

    // Try to get user ID from localStorage if not set in service
    let userId = this.userId;
    if (!userId) {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        userId = userData.id || null;
      } catch (e) {
        // Failed to get user data
      }
    }

    // Try to get token from localStorage if not set in service
    let token = this.userToken;
    if (!token) {
      try {
        token = localStorage.getItem('authToken');
      } catch (e) {
        // Failed to get token
      }
    }

    // Events that should automatically include session source
    const eventsWithSource = ['early_access_button_clicked', 'user_registered'];
    
    // If this event should include source and it's not already provided, get it from sessionStorage
    let sourceParams = tradeData.registration_source || tradeData.source || null;
    if (eventsWithSource.includes(eventType) && !sourceParams) {
      try {
        const stored = sessionStorage.getItem('registration_source');
        if (stored) {
          sourceParams = JSON.parse(stored);
        }
      } catch (e) {
        // sessionStorage not available or invalid JSON
      }
    }

    // Build event data with source if available
    const eventData = {
      session_id: sessionId,
      parameters: {
        ...tradeData,
        user_id: userId,
        timestamp: new Date().toISOString(),
        page_path: window.location.pathname,
        // Include source for events that should have it
        ...(eventsWithSource.includes(eventType) && sourceParams ? { 
          registration_source: sourceParams,
          source: sourceParams 
        } : {})
      }
    };

    // Send to Google Analytics (only if user is authenticated)
    if (typeof gtag !== 'undefined' && userId) {
      // eslint-disable-next-line no-undef
      gtag('event', eventType, {
        ...tradeData,
        custom_parameter_1: 'authenticated',
        custom_parameter_2: this.getUserType()
      });
    }

    // Send to PostHog for product analytics (skip when disabled)
    // This captures custom events with their properties for funnel analysis, etc.
    if (!this.isLocalHost) {
      try {
        const landingPageVersion = this.getLandingPageVersion(window.location.pathname, window.location.search);
        const posthogProperties = {
          ...tradeData,
          session_id: sessionId,
          user_type: this.getUserType(),
          page_path: window.location.pathname,
          landing_page_version: landingPageVersion || undefined,
          app_env: isLocalHost ? 'local' : undefined,
          // Include source for events that should have it
          ...(eventsWithSource.includes(eventType) && sourceParams ? { 
            registration_source: sourceParams,
            source: sourceParams 
          } : {})
        };
        
        posthog.capture(eventType, posthogProperties);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog: Event captured', { event: eventType, properties: posthogProperties });
        }
      } catch (error) {
        console.error('PostHog capture error:', error);
      }
    } else if (process.env.NODE_ENV === 'development') {
      console.log('PostHog: Skipped event (localhost)', { event: eventType });
    }

    // Always send to backend - backend will extract user ID from token if available
    // This allows tracking even if user ID isn't set in the service yet
    this.sendToBackend(eventType, eventData, token);
  }

  /**
   * Get user type for analytics
   */
  getUserType() {
    if (!this.userId) return 'anonymous';
    
    // You can enhance this based on your user data
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    if (userData.subscriptionStatus === 'active') return 'paid';
    if (userData.trialEndDate) return 'trial';
    return 'registered';
  }
  
  /**
   * Get readable page name from path
   */
  getPageName(pagePath) {
    // Remove query params for matching
    const cleanPath = pagePath.split('?')[0];
    
    const pathMap = {
      '/': 'Home',
      '/home': 'Home',
      '/landing': 'Landing',
      '/features': 'Features',
      '/pricing': 'Pricing',
      '/trade': 'Trade',
      '/register': 'Register',
      '/login': 'Login',
      '/settings': 'Settings',
      '/subscription-success': 'Subscription Success',
      '/forgot-password': 'Forgot Password',
      '/reset-password': 'Reset Password',
      '/legal': 'Legal',
      '/terms': 'Terms',
      '/privacy': 'Privacy',
      '/risk-disclosure': 'Risk Disclosure',
      '/contact': 'Contact',
      '/learn': 'Learn',
      '/blog': 'Blog',
      '/notify': 'Notifications',
      '/connected': 'Connected',
      '/onboarding': 'Onboarding',
      [DASHBOARD_ROUTES.overview]: 'Portfolio',
      [DASHBOARD_ROUTES.health]: 'Health',
      [DASHBOARD_ROUTES.performance]: 'Performance',
      [DASHBOARD_ROUTES.research]: 'Research',
      [DASHBOARD_ROUTES.alerts]: 'Alerts',
      [DASHBOARD_ROUTES.watchlists]: 'Watchlists',
      [DASHBOARD_ROUTES.journals]: 'Journals',
      [DASHBOARD_ROUTES.ai]: 'PrecisionAI',
      [DASHBOARD_ROUTES.tradingRules]: 'Trading Rules',
      [DASHBOARD_ROUTES.journalTemplate]: 'Journal Template',
      [DASHBOARD_ROUTES.settings]: 'Settings',
      [DASHBOARD_ROUTES.trade]: 'Trade',
      [DASHBOARD_ROUTES.marketOverview]: 'Market Overview',
      [DASHBOARD_ROUTES.marketRelativeStrength]: 'Relative Strength',
      [DASHBOARD_ROUTES.marketPositions]: 'Positions Overview',
      [DASHBOARD_ROUTES.marketResearch]: 'Research',
      [DASHBOARD_ROUTES.backtest]: 'Backtest',
    };
    
    // Check exact match first
    if (pathMap[cleanPath]) {
      return pathMap[cleanPath];
    }

    // Public posts are /blog/:slug. Editor and generation routes stay generic.
    if (cleanPath.startsWith('/blog/')) {
      const slug = cleanPath.slice('/blog/'.length).split('/')[0];
      if (slug && slug !== 'edit' && slug !== 'generating') {
        return `Blog · ${slug
          .split('-')
          .filter(Boolean)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')}`;
      }
    }
    
    // Handle admin routes
    if (cleanPath.startsWith('/admin/')) {
      const adminSection = cleanPath.replace('/admin/', '').split('/')[0];
      if (adminSection === 'bug-reports') {
        return 'Admin - Bug Reports';
      }
      // Map common admin sections
      const adminMap = {
        'sessionActivities': 'Admin - Analytics',
        'accountOverview': 'Admin - Account Overview',
        'support': 'Admin - Support',
        'users': 'Admin - Users',
        'billing': 'Admin - Billing',
        'settings': 'Admin - Settings'
      };
      return adminMap[adminSection] || `Admin - ${adminSection.charAt(0).toUpperCase() + adminSection.slice(1)}`;
    }

    // Nested dashboard routes that are not in the exact map (keep "Dashboard" from
    // collapsing /dashboard/watchlists and /dashboard/market into the same name).
    if (cleanPath === '/dashboard' || cleanPath.startsWith('/dashboard/')) {
      const rest = cleanPath.replace(/^\/dashboard\/?/, '');
      if (!rest) return 'Portfolio';
      return rest
        .split('/')
        .filter(Boolean)
        .map((segment) =>
          segment
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        )
        .join(' · ');
    }
    
    // Try to generate a readable name from the path
    if (cleanPath.startsWith('/')) {
      const pathParts = cleanPath.replace(/^\//, '').split('/');
      if (pathParts.length > 0 && pathParts[0]) {
        return pathParts[0]
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
    }
    
    return 'Unknown';
  }
  
  /**
   * Track scroll depth on a page
   * Updates the page_visit record with current scroll percentage
   * Call this from pages that need scroll tracking (Landing, Features)
   * @param {string} pagePath - The current page path
   * @param {number} scrollPercent - Scroll percentage (0-100)
   */
  trackScrollDepth(pagePath, scrollPercent) {
    if (!this.isInitialized) return;
    
    // Round to nearest 25% milestone (0, 25, 50, 75, 100)
    const milestone = Math.floor(scrollPercent / 25) * 25;
    const scrollKey = `${pagePath}_${milestone}`;
    
    // Only track each milestone once per page view
    if (this.scrollTracking.has(scrollKey)) {
      return;
    }
    
    this.scrollTracking.set(scrollKey, true);
    
    // Update the page_visit record with current scroll percentage
    // This updates the existing record instead of creating new events
    const pagePathKey = pagePath.split('?')[0];
    
    // Get page state (accumulated time and max scroll)
    const pageState = this.pageStates.get(pagePathKey) || {
      accumulatedTime: 0,
      maxScroll: 0,
      lastVisitStart: Date.now()
    };
    
    // Find the correct page start time for current visit
    let currentVisitStart = null;
    for (const [pageKey, startTime] of this.pageStartTimes.entries()) {
      const keyPath = pageKey.split('?')[0];
      if (keyPath === pagePathKey || keyPath === pagePath) {
        currentVisitStart = startTime;
        break;
      }
    }
    
    // Calculate time spent on current visit
    const timeOnCurrentVisit = currentVisitStart 
      ? Math.floor((Date.now() - currentVisitStart) / 1000) 
      : 0;
    
    // Update max scroll if this milestone is higher
    const newMaxScroll = Math.max(pageState.maxScroll, milestone);
    
    // Calculate total accumulated time (previous accumulated + current visit time)
    const totalAccumulatedTime = pageState.accumulatedTime + timeOnCurrentVisit;
    
    // Update page state
    this.pageStates.set(pagePathKey, {
      accumulatedTime: totalAccumulatedTime,
      maxScroll: newMaxScroll,
      lastVisitStart: currentVisitStart || pageState.lastVisitStart
    });
    // Scroll is stored in-memory and flushed with the page_visit on navigation/unload.
    // Sending a new page_visit per 25% milestone created duplicate rows when the
    // backend inserts instead of upserting.
  }
  
  /**
   * Reset scroll tracking for a new page
   */
  resetScrollTracking() {
    this.scrollTracking.clear();
  }

  /**
   * Send data to backend for custom analytics
   * @param {boolean} useBeacon - If true, use sendBeacon for reliable delivery (e.g., on page unload)
   */
  async sendToBackend(eventType, data, tokenOverride = null, useBeacon = false) {
    // Skip during pre-rendering to avoid network errors
    if (isPrerendering()) return;
    
    try {
      // Validate eventType before sending
      if (!eventType || typeof eventType !== 'string' || eventType.trim() === '') {
        console.error('Cannot send analytics event: invalid eventType', eventType);
        return;
      }

      // Get token from parameter, stored instance, or fall back to authToken in localStorage
      let token = tokenOverride || this.userToken;
      if (!token) {
        try {
          token = localStorage.getItem('authToken');
        } catch (e) {
          // Failed to get token
        }
      }

      const payload = {
        event_type: eventType,
        data: data,
        user_agent: navigator.userAgent,
        referrer: document.referrer,
        // eslint-disable-next-line no-restricted-globals
        screen_resolution: `${screen.width}x${screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`
      };

      // Debug logging removed for cleaner console output

      // Use sendBeacon for critical events (like session duration) that need to fire on page unload
      if (useBeacon && navigator.sendBeacon) {
        const url = `${process.env.REACT_APP_BACKEND_URL}/analytics/track`;
        // Note: sendBeacon doesn't support custom headers, so we include token in the payload
        if (token) {
          payload.auth_token = token;
        }
        const beaconBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(url, beaconBlob);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/analytics/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.warn('Analytics tracking failed:', response.status, errorText);
      }
    } catch (error) {
      console.error('Analytics tracking error:', error);
      if (process.env.NODE_ENV === 'production' && !isPrerendering()) {
        Sentry.captureException(error, { extra: { eventType } });
      }
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties) {
    if (typeof gtag !== 'undefined') {
      // eslint-disable-next-line no-undef
      gtag('config', this.gaId, {
        user_properties: properties
      });
    }
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
